import React from "react";
import { REQUEST_AND_ASSIGNMENT_TASK_TYPES, TaskType } from "./prop-types";
import { renderToString } from "react-dom/server";
import { Excavator } from "@procore/core-icons";
import { Link, useI18nContext } from "@procore/core-react";

const BASE_KEY = "views.company.workforce_planning";
const GANTT_TOOLTIP_KEY = `${BASE_KEY}.gantt.tooltip`;

export const GanttTooltipComponent = ({ ...props }: any) => {
   const I18nFromContext = useI18nContext();
   const {
      I18n: I18nProp,
      dateRangeString,
      name,
      type,
      projectName,
      projectColor,
      projectJobNumber,
      categoryName,
      jobTitleColor,
      jobTitleName,
      startTime,
      endTime,
      isTaskWithTime,
      isRequest,
      isEquipment,
      taskName,
      taskTimeAllocation,
      instructionText,
      workScopeText,
      workingDayClass,
      paidShiftHours,
      onClickResourceName,
   } = props;
   const I18n = I18nProp || I18nFromContext;
   const excavatorIcon = renderToString(<Excavator size="sm" color="#4f5964" />);

   if (type === TaskType.PROJECT) {
      const projectNumberStr = projectJobNumber ? `(${projectJobNumber})` : "";
      const renderedProjectName = `${name} ${projectNumberStr}`;
      return (
         <div className="gantt-project-tooltip">
            <div className="gantt-project-tooltip__header">
               <span
                  className="gantt-project-tooltip-color-icon"
                  style={{ color: projectColor }}
               ></span>
               <b>{renderedProjectName}</b>
            </div>
            <hr />
            <div className="gantt-project-tooltip__footer">
               <div className="flex">
                  <div className="gantt-project-tooltip-date">
                     <b>{I18n.t(`${GANTT_TOOLTIP_KEY}.dates`)}</b>
                     <br />
                     {dateRangeString}
                  </div>
                  <div className="gantt-project-tooltip-daily">
                     <b>{I18n.t(`${GANTT_TOOLTIP_KEY}.times`)}</b>
                     <br />
                     {startTime} - {endTime}
                  </div>
               </div>
            </div>
         </div>
      );
   } else if (REQUEST_AND_ASSIGNMENT_TASK_TYPES.has(type)) {
      return (
         <div className="gantt-project-tooltip">
            <div className="gantt-project-tooltip__header">
               <span
                  className="gantt-project-tooltip-color-icon"
                  style={{
                     display: isEquipment ? "none" : "inline",
                     color: jobTitleColor,
                  }}
               ></span>
               <img
                  src={`data:image/svg+xml;base64,${btoa(excavatorIcon)}`}
                  style={{
                     display: isEquipment ? "inline" : "none",
                     margin: "2px 4px 2px 2px",
                  }}
               />
               <b>
                  <div className={isRequest ? "requestPill" : "gantt-tooltip-assignment-header"}>
                     {onClickResourceName != null ? (
                        <Link onClick={onClickResourceName}>{taskName}</Link>
                     ) : (
                        <div>{taskName}</div>
                     )}
                  </div>
               </b>
               <span className="job-title">{jobTitleName}</span>
            </div>
            <hr />
            <div className="gantt-project-tooltip__footer">
               <div>
                  <b>
                     {projectName} ({projectJobNumber})
                  </b>
               </div>
               <div className="flex">
                  <div className="gantt-project-tooltip-date">
                     <b>{I18n.t(`${GANTT_TOOLTIP_KEY}.dates`)}</b>
                     <br />
                     {dateRangeString}
                  </div>
                  <div className="gantt-project-tooltip-daily">
                     <b>
                        {startTime && endTime
                           ? I18n.t(`${GANTT_TOOLTIP_KEY}.times`)
                           : I18n.t(`${GANTT_TOOLTIP_KEY}.assignment_allocation`)}
                     </b>
                     <br />
                     {isTaskWithTime
                        ? taskTimeAllocation
                        : `${taskTimeAllocation}% (${
                             (paidShiftHours * Number(taskTimeAllocation)) / 100
                          } hours)`}
                  </div>
               </div>
               <div>
                  <div className="gantt-project-tooltip-date">
                     <b>{I18n.t(`${BASE_KEY}.work_days`)}</b>
                     <br />
                     <div className="work-days">
                        <div className={workingDayClass!(0)}>
                           {I18n.t(`${BASE_KEY}.sunday_abbrev`)}
                        </div>
                        <div className={workingDayClass!(1)}>
                           {I18n.t(`${BASE_KEY}.monday_abbrev`)}
                        </div>
                        <div className={workingDayClass!(2)}>
                           {I18n.t(`${BASE_KEY}.tuesday_abbrev`)}
                        </div>
                        <div className={workingDayClass!(3)}>
                           {I18n.t(`${BASE_KEY}.wednesday_abbrev`)}
                        </div>
                        <div className={workingDayClass!(4)}>
                           {I18n.t(`${BASE_KEY}.thursday_abbrev`)}
                        </div>
                        <div className={workingDayClass!(5)}>
                           {I18n.t(`${BASE_KEY}.friday_abbrev`)}
                        </div>
                        <div className={workingDayClass!(6)}>
                           {I18n.t(`${BASE_KEY}.saturday_abbrev`)}
                        </div>
                     </div>
                  </div>
               </div>
               <div>
                  {instructionText && (
                     <div className="gantt-project-tooltip-instruction-text">
                        <b>{I18n.t(`${BASE_KEY}.assignment_instructions`)}</b>
                        <br />
                        {instructionText}
                        <br />
                     </div>
                  )}
                  {workScopeText && (
                     <div className="gantt-project-tooltip-work-scope-text">
                        <b>{I18n.t(`${BASE_KEY}.work_scope`)}</b>
                        <br />
                        {workScopeText}
                        <br />
                     </div>
                  )}
               </div>
            </div>
         </div>
      );
   } else if (type === TaskType.CATEGORY || type === TaskType.SUBCATEGORY) {
      return (
         <div className="gantt-project-tooltip">
            <div className="gantt-project-tooltip__header">
               <span
                  className="gantt-project-tooltip-color-icon"
                  style={{ color: projectColor }}
               ></span>
               <b>
                  {projectName} ({projectJobNumber})
               </b>
            </div>
            <hr />
            <div className="gantt-project-tooltip__footer">
               <div>
                  <b>
                     {categoryName ? `${categoryName}, ` : ""}
                     {name}
                  </b>
               </div>
               <div className="gantt-project-tooltip-date">
                  <b>{I18n.t(`${GANTT_TOOLTIP_KEY}.dates`)}</b>
                  <br />
                  {dateRangeString}
               </div>
            </div>
         </div>
      );
   } else return "";
};
